import type { BadgeVariant } from '@/components/ui/badge';

/**
 * Mirrors `AuditActorRealm` from `src/lib/schemas/auditEvents.ts` (root app).
 * Duplicated rather than imported: `actor_realm` is an untrusted `text`
 * column at runtime, and this app has no path alias into root `src/`.
 */
export type AuditActorRealm = 'merchant' | 'tagada' | 'partner' | 'machine' | 'unknown';

/**
 * i18n key for a catalogued action verb (`AUDIT_ACTIONS` in
 * `src/lib/audit/actions.ts`). Dots become underscores because i18next
 * treats `.` as a nested-key separator — a raw `funnel.routing_published`
 * key would resolve as `{ funnel: { routing_published: ... } }` and miss.
 */
export function getActionLabelKey(action: string): string {
  return `auditTrail.actions.${action.replace(/\./g, '_')}`;
}

/**
 * Readable fallback for an action with no catalogue entry (a raw tRPC
 * router path, e.g. `plugins.updateInstanceConfig`). Not localized: these
 * are facade paths, not product copy, so `Plugins · Update instance
 * config` is shown as-is.
 */
export function humanizeAction(action: string): string {
  return action.split('.').filter(Boolean).map(humanizeSegment).join(' · ');
}

function humanizeSegment(segment: string): string {
  const words = segment.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export type AuditRealmBadge = {
  labelKey: string;
  tooltipKey?: string;
  variant: BadgeVariant;
};

/**
 * `tagada` provably also covers partner/reseller operators acting on a
 * merchant's account (verified in prod), and no rows currently use the
 * `partner` realm — hence the "Tagada / Partner" copy and its tooltip.
 * Do not narrow it to "Tagada support".
 */
export function getRealmBadge(realm: string): AuditRealmBadge {
  switch (realm) {
    case 'merchant':
      return { labelKey: 'auditTrail.realm.merchant', variant: 'blue' };
    case 'tagada':
      return {
        labelKey: 'auditTrail.realm.tagada',
        tooltipKey: 'auditTrail.realm.tagadaTooltip',
        variant: 'purple',
      };
    case 'partner':
      return { labelKey: 'auditTrail.realm.partner', variant: 'indigo' };
    case 'machine':
      return { labelKey: 'auditTrail.realm.machine', variant: 'gray' };
    case 'unknown':
    default:
      return { labelKey: 'auditTrail.realm.unknown', variant: 'outline' };
  }
}

/**
 * The `fieldsTouched` names an action recorded without their values (typically a
 * credential rotation). Returns null when there is nothing renderable.
 *
 * Length is checked AFTER filtering: `context` is untrusted JSON, and an array of
 * non-strings would otherwise yield a truthy `[]`, rendering an empty "fields
 * modified" section instead of falling through to "no detail recorded".
 */
export function getFieldsTouched(context: Record<string, unknown>): string[] | null {
  const value = context.fieldsTouched;
  if (!Array.isArray(value)) return null;

  const fields = value.filter((field): field is string => typeof field === 'string');
  return fields.length > 0 ? fields : null;
}

type AuditTruncatedValue = {
  __truncated: true;
  __originalBytes?: number;
  __serializationFailed?: boolean;
};

export function isTruncated(value: unknown): value is AuditTruncatedValue {
  return (
    typeof value === 'object' && value !== null && (value as Record<string, unknown>).__truncated === true
  );
}

export type FormattedAuditValue = {
  kind: 'truncated' | 'empty' | 'primitive' | 'json';
  text: string;
  bytes?: number;
};

/**
 * Renders one side of a `changes` diff entry. Does not decide between
 * "not set" (key absent, e.g. `from` on a create-shaped diff) and
 * "cleared" (key present with value `null`) — both produce `kind: 'empty'`
 * here, since only the caller holds the raw object and can check key
 * presence with `in`.
 */
export function formatAuditValue(value: unknown): FormattedAuditValue {
  if (isTruncated(value)) {
    return { kind: 'truncated', text: '', bytes: value.__originalBytes };
  }

  if (value === null || value === undefined) {
    return { kind: 'empty', text: '' };
  }

  if (typeof value === 'object') {
    return { kind: 'json', text: JSON.stringify(value, null, 2) };
  }

  return { kind: 'primitive', text: String(value) };
}
